import { useEffect, useState } from "react";
import { KeyRound, ShieldCheck, ShieldX, Copy, RefreshCw, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";

const Chip = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[9px] font-black tracking-widest ${ok ? "bg-[#2ECC71]/15 text-[#2ECC71]" : "bg-[#C0392B]/15 text-[#E74C3C]"}`}>
    {ok ? <ShieldCheck size={10} /> : <ShieldX size={10} />} {children}
  </span>
);

const OwnerPinReset = () => {
  const [pc, setPc] = useState("");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [busy, setBusy] = useState(false);
  const [changedAt, setChangedAt] = useState(null);
  const clear = () => { setPc(""); setP1(""); setP2(""); };
  const digits = (v) => v.replace(/\D/g, "").slice(0, 6);
  const submit = async () => {
    if (p1 !== p2) { toast.error("New PINs don't match"); clear(); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/reset-owner-pin", { passcode: pc, new_pin: p1 });
      setChangedAt(r.data.pin_changed_at);
      toast.success("Owner staff PIN reset — all previous owner tablet sessions are signed out");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not reset PIN");
    } finally { setBusy(false); clear(); }
  };
  return (
    <div data-testid="owner-pin-reset-card" className="rounded-2xl bg-[#121212] border border-[#D4AF37]/25 p-5 mt-6">
      <p className="text-xs font-black tracking-[0.2em] text-[#D4AF37] flex items-center gap-2"><LifeBuoy size={14} /> RESET OWNER STAFF PIN</p>
      <p className="text-xs text-white/45 mt-2">Recovery for a forgotten owner tablet PIN. Requires your admin passcode again. Values are hashed server-side and never shown or stored.</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
        <input data-testid="ownerreset-passcode" type="password" inputMode="numeric" autoComplete="off" placeholder="Admin passcode" value={pc}
          onChange={(e) => setPc(e.target.value)} className="bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]/70" />
        <input data-testid="ownerreset-new-pin" type="password" inputMode="numeric" autoComplete="off" placeholder="New owner PIN (4-6 digits)" value={p1}
          onChange={(e) => setP1(digits(e.target.value))} className="bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]/70" />
        <input data-testid="ownerreset-confirm-pin" type="password" inputMode="numeric" autoComplete="off" placeholder="Confirm new PIN" value={p2}
          onChange={(e) => setP2(digits(e.target.value))} className="bg-[#1A1A20] border border-white/15 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]/70" />
      </div>
      <div className="flex items-center gap-3 mt-3">
        <button data-testid="ownerreset-submit" disabled={busy || pc.length < 1 || p1.length < 4 || p2.length < 4} onClick={submit}
          className="rounded-xl bg-[#D4AF37] text-black font-black py-3 px-6 text-sm tracking-wide disabled:opacity-40">{busy ? "RESETTING…" : "RESET PIN"}</button>
        {changedAt && <span data-testid="ownerreset-changed-at" className="text-xs text-[#2ECC71]/80 font-bold">PIN changed {new Date(changedAt).toLocaleString()}</span>}
      </div>
    </div>
  );
};

export const AdminAccess = () => {
  const [keys, setKeys] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [log, setLog] = useState([]);
  const [deniedOnly, setDeniedOnly] = useState(false);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadKeys = () => api.get("/admin/access/keys").then((r) => setKeys(r.data)).catch(() => {});
  const loadIntegrations = () => api.get("/admin/access/integrations").then((r) => setIntegrations(r.data)).catch(() => {});
  const loadLog = (denied) => api.get(`/admin/access/log?limit=100&denied_only=${denied}`).then((r) => setLog(r.data)).catch(() => {});

  useEffect(() => { loadKeys(); loadIntegrations(); loadLog(false); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/access/keys", { name });
      setNewKey(r.data);
      setName("");
      loadKeys();
      toast.success("Key generated — copy it now, it won't be shown again");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate key"); }
    setBusy(false);
  };

  const revoke = async (keyId) => {
    try {
      await api.post(`/admin/access/keys/${keyId}/revoke`);
      toast.success("Key revoked");
      loadKeys();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not revoke"); }
  };

  return (
    <div data-testid="admin-access" className="space-y-6">
      {/* API keys */}
      <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold flex items-center gap-1.5"><KeyRound size={12} /> API KEYS — WHO MAY CALL THE API</p>
        <p className="text-[10px] text-white/40 mt-1">The Passport app has its own built-in key (it ships inside the public app, so it stops outside scripts — not a determined inspector). Generate extra named keys for partners/tools and revoke them anytime. Full key is shown ONCE.</p>
        <div className="flex items-center gap-2 mt-3">
          <input data-testid="access-generate-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
            placeholder="Key name (e.g. Website checkout)" onKeyDown={(e) => e.key === "Enter" && generate()}
            className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/10 rounded-full px-4 h-10 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
          <button data-testid="access-generate-btn" onClick={generate} disabled={busy || name.trim().length < 3}
            className="h-10 px-5 rounded-full bg-[#D4AF37] text-black text-[10px] font-black tracking-widest disabled:opacity-50 shrink-0">GENERATE</button>
        </div>
        {newKey && (
          <div data-testid="access-new-key" className="mt-3 rounded-xl border border-[#D4AF37]/50 bg-[#1C160A] p-3">
            <p className="text-[9px] font-black tracking-widest text-[#E74C3C]">COPY THIS KEY NOW — IT WILL NEVER BE SHOWN AGAIN</p>
            <div className="flex items-center gap-2 mt-2">
              <code className="flex-1 min-w-0 truncate text-[11px] text-[#F0C742] bg-black/40 rounded-lg px-3 py-2">{newKey.key}</code>
              <button data-testid="access-copy-key" onClick={() => { navigator.clipboard.writeText(newKey.key); toast.success("Copied"); }}
                className="h-9 px-3 rounded-full border border-[#D4AF37]/50 text-[#D4AF37] shrink-0"><Copy size={13} /></button>
            </div>
          </div>
        )}
        <div className="mt-3 divide-y divide-white/5">
          {keys.length === 0 && <p className="text-[10px] text-white/35 py-2">No extra keys yet — only the built-in app key is active.</p>}
          {keys.map((k) => (
            <div key={k.key_id} data-testid={`access-key-row-${k.key_id}`} className="flex items-center justify-between gap-2 py-2 text-xs flex-wrap">
              <div className="min-w-0">
                <p className="font-bold truncate">{k.name} <span className="text-white/35 font-mono text-[10px]">({k.prefix}…)</span></p>
                <p className="text-[9px] text-white/40">created {String(k.created_at).slice(0, 16)} · {k.use_count || 0} uses · last used {k.last_used_at ? String(k.last_used_at).slice(0, 16) : "never"}</p>
              </div>
              {k.revoked_at
                ? <span className="text-[9px] font-black text-[#E74C3C] tracking-widest shrink-0">REVOKED</span>
                : <button data-testid={`access-revoke-${k.key_id}`} onClick={() => revoke(k.key_id)}
                    className="text-[9px] font-black tracking-widest text-[#E74C3C] border border-[#E74C3C]/40 rounded-full px-3 py-1.5 shrink-0">REVOKE</button>}
            </div>
          ))}
        </div>
      </div>

      {/* connected integrations */}
      <div data-testid="access-integrations" className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold">CONNECTED INTEGRATIONS</p>
        <div className="mt-2 divide-y divide-white/5">
          {integrations.map((i, idx) => (
            <div key={idx} className="flex items-center justify-between gap-2 py-2 text-xs flex-wrap">
              <div className="min-w-0">
                <p className="font-bold truncate">{i.name}</p>
                {i.detail && <p className="text-[9px] text-white/40">{i.detail}</p>}
              </div>
              <Chip ok={i.configured}>{i.configured ? "CONFIGURED" : "NOT SET"}</Chip>
            </div>
          ))}
        </div>
      </div>

      {/* access log */}
      <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold">ACCESS LOG — LAST 7 DAYS</p>
          <div className="flex items-center gap-2">
            <button data-testid="access-denied-toggle" onClick={() => { const d = !deniedOnly; setDeniedOnly(d); loadLog(d); }}
              className={`text-[9px] font-black tracking-widest rounded-full px-3 py-1.5 border ${deniedOnly ? "bg-[#E74C3C] text-black border-[#E74C3C]" : "text-white/50 border-white/15"}`}>
              DENIED ONLY
            </button>
            <button data-testid="access-log-refresh" onClick={() => loadLog(deniedOnly)} aria-label="Refresh log"
              className="w-8 h-8 rounded-full border border-white/15 text-white/60 flex items-center justify-center"><RefreshCw size={12} /></button>
          </div>
        </div>
        <div data-testid="access-log-table" className="mt-2 divide-y divide-white/5 max-h-96 overflow-y-auto">
          {log.length === 0 && <p className="text-[10px] text-white/35 py-2">No entries{deniedOnly ? " — nothing has been denied" : ""}.</p>}
          {log.map((e, i) => (
            <div key={i} data-testid={`access-log-row-${i}`} className="flex items-center gap-2 py-1.5 text-[10px]">
              <span className="text-white/40 shrink-0 tabular-nums">{String(e.ts).slice(5, 16).replace("T", " ")}</span>
              <span className="text-white/50 shrink-0">{e.ip}</span>
              <span className="flex-1 min-w-0 truncate font-mono text-white/70">{e.method} {e.path}</span>
              <span className="text-white/40 shrink-0">{e.key}</span>
              <span className={`font-black shrink-0 ${e.status === "denied" ? "text-[#E74C3C]" : "text-[#2ECC71]"}`}>{e.status.toUpperCase()}</span>
            </div>
          ))}
        </div>
      </div>
      <OwnerPinReset />
    </div>
  );
};
